// The query string patron hands to the auth URL when it bounces an
// unauthenticated visitor. It is rebuilt from scratch rather than forwarded,
// so anything not named here is dropped for the rest of the session.
//
// Two call sites share this: the bootstrap 401 path (src/index.tsx) and the
// in-app path (src/lib/auth.js). They were hand-duplicated and had already
// drifted apart.

const CARRIED_PARAMS = ['tok', 'ids'];

// A scan arrives on a printed code, so the visitor is usually not signed in yet
// and the attribution has to survive the bounce or the scan can never be
// counted. Restricted to plain tokens: these values end up on an outbound URL,
// and the contract specifies the same shape.
const ATTRIBUTION_PARAMS = ['utm_medium', 'utm_source'];
const ATTRIBUTION_TOKEN = /^[a-z0-9_]{1,32}$/i;

interface AuthRedirectOptions {
  forceReAuth?: boolean;
}

export const buildAuthRedirectSearch = (
  search: string,
  { forceReAuth = false }: AuthRedirectOptions = {}
): string => {
  const incoming = new URLSearchParams(search);
  const carried = new URLSearchParams();

  CARRIED_PARAMS.forEach((name) => {
    const value = incoming.get(name);
    if (value) carried.append(name, value);
  });

  // Matched case-insensitively and re-emitted canonical: some QR encoders
  // uppercase the whole URL, and the analytics library only maps a lowercase
  // "utm_" prefix into context.campaign.
  const lowercased: Record<string, string> = {};
  incoming.forEach((value, name) => {
    lowercased[name.toLowerCase()] = value;
  });

  ATTRIBUTION_PARAMS.forEach((name) => {
    const value = lowercased[name]?.toLowerCase();
    if (value && ATTRIBUTION_TOKEN.test(value)) carried.append(name, value);
  });

  carried.append('skip_mobile_redirect', 'true');
  if (forceReAuth) carried.append('reauthenticate', 'true');

  return carried.toString();
};
